import { useEffect, useState } from "react";
import { protectedResources } from "../../authConfig";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import useWindowDimensions from "../../hooks/windowDimensions";
import { useMsal } from "@azure/msal-react";
import ExportDataComponent from "./ExportComponent"
import "./Reports.scss";

type ReportsComponentProps = {
  title: string;
  endpoint: string;
};

// fewer rows per page on smaller screens, matching styles/variables.scss breakpoints
const getPageSize = (width: number): number => {
  if (width < 500) return 10;
  if (width < 768) return 25;
  return 50;
};

// "skillGroupName" -> "Skill Group Name"
const formatHeader = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

const ReportsComponent: React.FC<ReportsComponentProps> = ({ title, endpoint }) => {
  const { instance } = useMsal();
  const [account] = useState(instance.getActiveAccount());
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiCFSkillPath.scopes.read,
  });
  const [loading, setLoading] = useState(false);
  const [callErr, setErr] = useState(null);
  const [reportData, setReportData] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const { width } = useWindowDimensions();
  const pageSize = getPageSize(width);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  useEffect(() => {
    setReportData([]);
    setPage(0);
    setLoading(true);
    execute("GET", endpoint)
      .then((response) => {
        if (response.error) {
          return;
        }
        setReportData(Array.isArray(response) ? response : []);
      })
      .catch((err) => {
        setErr(err);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, account]);

  const columns = reportData.length > 0
    ? Object.keys(reportData[0]).filter(col => !col.endsWith("Id"))
    : [];

  const pageCount = Math.ceil(reportData.length / pageSize);
  const pagedData = reportData.slice(page * pageSize, page * pageSize + pageSize);

  const getFilename = (reportTitle: string): string => {
    const datePart = new Date().toISOString().slice(0, 10);
    return `${reportTitle.replace(/\s+/g, "_")}_${datePart}.csv`;
  };

  return <>
    <div className="max-width">
      <h2 className="m-b-1 m-t-1">{title}</h2>
      {loading && <p>Loading...</p>}
      {!loading && reportData.length === 0 && <p>No data to display.</p>}
      {!loading && reportData.length > 0 && (
        <div className="dis-flex dis-flex-jst-space-between dis-flex-aln-center">
          <span className="fnt-sz-sm">{reportData.length} records found.</span>
          <ExportDataComponent data={reportData} filename={getFilename(title)} ></ExportDataComponent>
        </div>
      )}

      <div className="reports-table-container">
        <table className="bdr-sld-med-gray">
          <thead>
            <tr>
              {columns.map(col =>
                <th key={col} className={typeof reportData[0][col] === "number" ? "aln-center" : undefined}>
                  {formatHeader(col)}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pagedData.map((row, i) =>
              <tr key={page * pageSize + i}>
                {columns.map(col =>
                  <td key={col} className={typeof row[col] === "number" ? "aln-center" : undefined}>
                    {row[col]}
                  </td>
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!loading && pageCount > 1 && (
        <div className="dis-flex dis-flex-jst-end dis-flex-aln-center m-t-1">
          <button
            className="btn-outlined-primary"
            onClick={() => setPage(p => Math.max(p - 1, 0))}
            disabled={page === 0}
          >
           <span className="fnt-sz-lrg">&lt;</span> 
          </button>
          <span className="m-l-1 m-r-1">Page {page + 1} of {pageCount}</span>
          <button
            className="btn-outlined-primary"
            onClick={() => setPage(p => Math.min(p + 1, pageCount - 1))}
            disabled={page >= pageCount - 1}
          >
            <span className="fnt-sz-lrg">&gt;</span> 
          </button>
        </div>
      )}
    </div>
  </>
}

export default ReportsComponent;